import { Circ, Expo, TimelineLite, TimelineMax } from "gsap";
import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import { Button, OutlinedButton } from "../gameobjects/Button";
import {AbstractGameState} from "./AbstractGameState";

import { PlayMode } from "html-living-framework";
import { isMobile } from "html-living-framework/lib/Utils/PlatformUtils";
import { Application } from "../Application";
import { texture } from "../core/AssetHelpers";
import { CaptionToken } from "../data/CaptionToken";
import * as PlanterGameModel from "../data/PlanterGameModel";
import * as VO from "../data/VO";
import { randomExhaust } from "../data/VO";
import { ColorOverlayFilter } from "../filters/ColorOverlayFilter";
import { CustomizedPlanter } from "../gameobjects/CustomizedPlanter";
import { CustomizeOptionButton } from "../gameobjects/CustomizeOptionButton";
import { CustomizeTab } from "../gameobjects/CustomizeTab";
import {PlanterCustomizeLayout} from "../layout/PlanterCustomizeLayout";
import { CaptionManager } from "../managers/CaptionManager";
import { PlanterPlantingIntroScreen } from "./PlanterPlantingIntroScreen";
import { PlanterPlantingScreen } from "./PlanterPlantingScreen";

const clickVO = isMobile() ? VO.PP_PROMPT01 : VO.PP_PROMPT02;
const choiceReactVO = randomExhaust([VO.PP_CUSTOM04, VO.PP_CUSTOM05, VO.PP_CUSTOM06, VO.PP_CUSTOM07]);

export class PlanterCustomizeScreen extends AbstractGameState {

    public static readonly NAME = "PlanterCustomizeScreen";

    public readonly bg = this.addChild(new Sprite());
    public readonly bgOverlay = this.addChild(new PIXI.Graphics());
    public readonly bench = this.addChild(new Sprite());
    public readonly tabsDropShadow = this.addChild(new Sprite());
    public readonly tabsContainer = this.addChild(new Container());
    public readonly tab = [
        PlanterGameModel.Tabs.Shape,
        PlanterGameModel.Tabs.Colour,
        PlanterGameModel.Tabs.Pattern,
        PlanterGameModel.Tabs.Stickers,
    ].map((tabType) => this.tabsContainer.addChild(new CustomizeTab(tabType)));
    public readonly continueButton = this.addChild(new OutlinedButton());
    public readonly shadow = this.addChild(new Sprite("shadow-wateringcan", {x: 0.5, y: 0.75}));
    public readonly planter = this.addChild(new CustomizedPlanter());
    public readonly homeButton = this.addChild(new OutlinedButton());

    protected analyticsScreenName: string = "6a Planter Party_Planter Create Screen";
    protected analyticsRoundName = "Planter Party";
    protected _subscriptions: any[] = [];
    protected hasPlayedDecorateVO = false;
    protected clickCount: number = 0;
    protected screenStartTime: number = 0;

    public init(pSize: PIXI.Point, pData?: any) {
        PlanterGameModel.GameState.reset();
        PlanterCustomizeLayout.call(this);
        this.homeButton.onClick.add(() => {
            this.trackEvent("gameMenuClick", {buttonName: "Home"});
            this.gotoNextState("MainMenu");
        });
        this.homeButton.clickSfx = "buttonclick";

        this.tab.forEach((t, i) => t.init(i === 0));
        this.tab.forEach((t, i) => t.enabled = (i === 0));
        this.tab.forEach((t, i) => t.zIndex = t.active ? this.tab.length : this.tab.length - i - 1);
        this._subscriptions.push(PubSub.subscribe("option selected", this.onOptionSelected.bind(this)));
        this._subscriptions.push(PubSub.subscribe("option cleared", this.onOptionCleared.bind(this)));
        this._subscriptions.push(PubSub.subscribe("tab activated", this.onTabActivated.bind(this)));

        this.continueButton.onClick.add(() => {
            this.idleTimeline?.restart(true);
            if (this.idleStartTime !== undefined) {
                const idleEndTime = new Date().valueOf();
                const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
                this.trackEvent("gameTOReEngage", {duration: idleDuration});
                this.idleStartTime = undefined;
            }
            this.trackEvent("gameMenuClick", {buttonName: this.continueButton.name.replace(/^\w/, (c) => c.toUpperCase())});
            if (!this.tab.find((t) => !t.visited)) {
                this.trackEvent("gameComponentComplete", {
                    componentType: "Choice",
                    componentName: "Create Planter",
                    duration: (new Date().valueOf() - this.screenStartTime) / 1000,
                });
                if (PlanterPlantingIntroScreen.visited) {
                    this.gotoNextState(PlanterPlantingScreen.NAME);
                }
                else {
                    this.gotoNextState(PlanterPlantingIntroScreen.NAME);
                }
            }
            else {
                for (let i = 0; i < this.tab.length; i++) {
                    if (this.tab[i].active && i < this.tab.length - 1 && this.tab[i + 1].enabled) {
                        this.tab[i].active = false;
                        this.tab[i + 1].active = true;
                        break;
                    }
                }
            }
        });
        this.continueButton.clickSfx = "buttonclick";

        this.continueButton.visible = false;

        this.planter.scale.set(1);
        this.planter.interactive = false;
        this.shadow.scale.set(2);
        this.shadow.position.copyFrom(this.planter.position);
        this.shadow.visible = false;

        if (pData?.fromMainMenu) {
            this.trackEvent("gameRoundStart", {roundNumber: Application.instance.round});
            PlanterGameModel.GameState.roundStartTime = new Date().valueOf();
        }

        this.trackEvent("gameComponentStart", {
            componentType: "Choice",
            componentName: "Create Planter",
        });
        this.screenStartTime = new Date().valueOf();
        super.init(pSize);
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
        const bgm = HLF.Application.instance.audio.getAudioTrack("planter_party_loop", HLF.AudioCategory.BGM) ?? HLF.Application.instance.audio.createAudioTrack("planter_party_loop", HLF.AudioCategory.BGM);
        if (bgm.isPlaying() === false) {
            bgm.setLooped(true);
            bgm.play();
        }
        Application.instance.voiceover.playVO([VO.PP_CUSTOM01, VO.PP_CUSTOM02], {
            priority: 2,
            callback: () => {
                this.idleTimeline.pause();
                this.idleTimeline.call(() => {
                    this.trackEvent("gameTimeoutPlay");
                    if (this.idleStartTime === undefined) {
                        this.idleStartTime = new Date().valueOf();
                    }

                    const hintContinueButton = () => {
                        if (this.continueButton.visible) {
                            this.continueButton.pulse();
                            this.continueButton.outlineFilter.enabled = true;
                            if (this.continueButton.name === "ConfirmBTN") {
                                Application.instance.voiceover.playVO(VO.PP_CUSTOM_TO07, {
                                    mode: PlayMode.New,
                                    callback: () => {
                                        this.continueButton.stopPulse();
                                        this.continueButton.outlineFilter.enabled = false;
                                        this.idleTimeline?.restart(true);
                                    },
                                    priority: -1,
                                });
                            }
                            else {
                                Application.instance.voiceover.playVO([clickVO, VO.PP_CUSTOM_TO05], {
                                    mode: PlayMode.New,
                                    callback: () => {
                                        this.continueButton.stopPulse();
                                        this.continueButton.outlineFilter.enabled = false;
                                        this.idleTimeline?.restart(true);
                                    },
                                    skipCC: true,
                                    priority: -1,
                                });
                                PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(VO.PP_CUSTOM_TO05, 0, {
                                    prompt: clickVO,
                                }));
                            }
                        }
                    };

                    if (this.tab[0].active) {
                        this.tab[0].buttons.forEach((b) => b.pulse());
                        Application.instance.voiceover.playVO([clickVO, VO.PP_CUSTOM_TO01], {
                            mode: PlayMode.New,
                            callback: () => {
                                this.tab[0].buttons.forEach((b) => b.stopPulse());
                                this.idleTimeline?.restart(true);
                                hintContinueButton();
                            },
                            skipCC: true,
                            priority: -1,
                        });
                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(VO.PP_CUSTOM_TO01, 0, {
                            prompt: clickVO,
                        }));
                    }
                    else if (this.tab[1].active) {
                        this.tab[1].buttons.forEach((b) => b.pulse());
                        Application.instance.voiceover.playVO([clickVO, VO.PP_CUSTOM_TO02], {
                            mode: PlayMode.New,
                            callback: () => {
                                this.tab[1].buttons.forEach((b) => b.stopPulse());
                                this.idleTimeline?.restart(true);
                                hintContinueButton();
                            },
                            skipCC: true,
                            priority: -1,
                        });
                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(VO.PP_CUSTOM_TO02, 0, {
                            prompt: clickVO,
                        }));
                    }
                    else if (this.tab[2].active) {
                        this.tab[2].buttons.forEach((b) => b.pulse());
                        Application.instance.voiceover.playVO([clickVO, VO.PP_CUSTOM_TO03], {
                            mode: PlayMode.New,
                            callback: () => {
                                this.tab[2].buttons.forEach((b) => b.stopPulse());
                                this.idleTimeline?.restart(true);
                                hintContinueButton();
                            },
                            skipCC: true,
                            priority: -1,
                        });
                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(VO.PP_CUSTOM_TO03, 0, {
                            prompt: clickVO,
                        }));
                    }
                    else if (this.tab[3].active) {
                        this.tab[3].buttons.forEach((b) => b.pulse());
                        Application.instance.voiceover.playVO([clickVO, VO.PP_CUSTOM_TO04], {
                            mode: PlayMode.New,
                            callback: () => {
                                this.tab[3].buttons.forEach((b) => b.stopPulse());
                                this.idleTimeline?.restart(true);
                                hintContinueButton();
                            },
                            skipCC: true,
                            priority: -1,
                        });
                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(VO.PP_CUSTOM_TO04, 0, {
                            prompt: clickVO,
                        }));
                    }

                    this.idleTimeline?.restart(true);
                });
                this.idleTimeline.restart(true);
            },
        });
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.position.set((pSize.x - 1024) / 2, 0);
        this.bgOverlay.clear();
        this.bgOverlay.beginFill(0x000000, 0.5);
        this.bgOverlay.drawRect(-this.x, 0, pSize.x, pSize.y);
        this.bgOverlay.endFill();
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
    }

    public destroy() {
        super.destroy();
        for (const subscription of this._subscriptions) {
            PubSub.unsubscribe(subscription);
        }
        this.idleTimeline?.kill();
        this.idleTimeline?.clear();
    }

    protected onTabActivated(pTopic: string, pData: CustomizeTab) {
        console.log(pTopic, pData.active);
        this.tab.forEach((t, i) => t.zIndex = this.tab.length - i - 1);
        pData.zIndex = this.tab.length;
        if (!this.tab.find((t) => !t.visited)) {
            this.continueButton.visible = true;
            this.continueButton.setLayoutTexture("ConfirmBTN", 0.5, 0.5);
        }
        // don't show arrow button on last tab if user skipped a tab
        else if (pData.tabType !== PlanterGameModel.Tabs.Stickers) {
            this.continueButton.visible = pData.visited;
        }
        else {
            this.continueButton.visible = false;
        }
        this.tab.forEach((t) => t.buttons.forEach((b) => b.stopPulse()));
        this.continueButton.stopPulse();
        this.continueButton.outlineFilter.enabled = false;
        this.idleTimeline?.restart(true);
        if (this.idleStartTime !== undefined) {
            const idleEndTime = new Date().valueOf();
            const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
            this.trackEvent("gameTOReEngage", {duration: idleDuration});
            this.idleStartTime = undefined;
        }
        this.trackEvent("gameMenuClick", {buttonName: pData.tabType.replace(/^\w/, (c) => c.toUpperCase())});
    }

    protected onOptionCleared(pTopic: string, pData: CustomizeOptionButton) {
        switch (pData.tab) {
            case PlanterGameModel.Tabs.Pattern:
                PlanterGameModel.GameState.pattern = PlanterGameModel.PlanterPattern.None;
                break;
            case PlanterGameModel.Tabs.Stickers:
                PlanterGameModel.GameState.sticker = PlanterGameModel.PlanterSticker.None;
                break;
            // shape and colour shouldn't be deselectable anyway
            case PlanterGameModel.Tabs.Shape:
            case PlanterGameModel.Tabs.Colour:
                this.continueButton.visible = false;
                break;
        }

        this.tab.forEach((t) => t.buttons.forEach((b) => b.stopPulse()));
        this.continueButton.stopPulse();
        this.continueButton.outlineFilter.enabled = false;
        this.idleTimeline?.restart(true);
        if (this.idleStartTime !== undefined) {
            const idleEndTime = new Date().valueOf();
            const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
            this.trackEvent("gameTOReEngage", {duration: idleDuration});
            this.idleStartTime = undefined;
        }
        PubSub.publishSync("planter update", PlanterGameModel.GameState);
    }

    protected onOptionSelected(pTopic: string, pData: CustomizeOptionButton) {
        this.idleTimeline?.restart(true);
        this.tab.forEach((t) => t.buttons.forEach((b) => b.stopPulse()));
        this.continueButton.stopPulse();
        this.continueButton.outlineFilter.enabled = false;
        if (this.idleStartTime !== undefined) {
            const idleEndTime = new Date().valueOf();
            const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
            this.trackEvent("gameTOReEngage", {duration: idleDuration});
            this.idleStartTime = undefined;
        }

        this.trackEvent("gameInteraction", {objectName: pData.name.replace(/^\w/, (c) => c.toUpperCase())});

        switch (pData.tab) {
            case PlanterGameModel.Tabs.Shape:
                PlanterGameModel.GameState.shape = Object.values(PlanterGameModel.PlanterShape)[pData.index];
                if (!this.hasPlayedDecorateVO) {
                    this.hasPlayedDecorateVO = true;
                    Application.instance.voiceover.playVO(VO.PP_CUSTOM03);
                }
                this.shadow.visible = true;
                this.tab[1].enabled = true;
                break;
            case PlanterGameModel.Tabs.Colour:
                PlanterGameModel.GameState.colour = Object.values(PlanterGameModel.PlanterColour)[pData.index];
                this.tab[2].enabled = true;
                break;
            case PlanterGameModel.Tabs.Pattern:
                PlanterGameModel.GameState.pattern = Object.values(PlanterGameModel.PlanterPattern)[pData.index];
                this.tab[3].enabled = true;
                break;
            case PlanterGameModel.Tabs.Stickers:
                PlanterGameModel.GameState.sticker = Object.values(PlanterGameModel.PlanterSticker)[pData.index];
                break;
        }

        PubSub.publishSync("planter update", PlanterGameModel.GameState);
        this.continueButton.visible = true;
        if (!this.tab.find((t) => !t.visited)) {
            this.continueButton.setLayoutTexture("ConfirmBTN", 0.5, 0.5);
        }
        // don't show arrow button on last tab if user skipped a tab
        else if (pData.tab === PlanterGameModel.Tabs.Stickers) {
            this.continueButton.visible = false;
        }

        this.clickCount++;
        if (this.clickCount % 3 === 0) {

            const voToPlay = [];

            switch (pData.icon.name) {
                case PlanterGameModel.PlanterShape.Bucket:
                    voToPlay.push(VO.PP_VESSEL01);
                    break;
                case PlanterGameModel.PlanterShape.RainBoot:
                    voToPlay.push(VO.PP_VESSEL02);
                    break;
                case PlanterGameModel.PlanterShape.CoffeeTin:
                    voToPlay.push(VO.PP_VESSEL03);
                    break;
                case PlanterGameModel.PlanterShape.WateringCan:
                    voToPlay.push(VO.PP_VESSEL04);
                    break;
                case PlanterGameModel.PlanterShape.PBJar:
                    voToPlay.push(VO.PP_VESSEL05);
                    break;
                case PlanterGameModel.PlanterColour.Orange:
                    voToPlay.push(VO.PP_COLOR01);
                    break;
                case PlanterGameModel.PlanterColour.Yellow:
                    voToPlay.push(VO.PP_COLOR02);
                    break;
                case PlanterGameModel.PlanterColour.Blue:
                    voToPlay.push(VO.PP_COLOR03);
                    break;
                case PlanterGameModel.PlanterColour.Red:
                    voToPlay.push(VO.PP_COLOR04);
                    break;
                case PlanterGameModel.PlanterColour.Green:
                    voToPlay.push(VO.PP_COLOR05);
                    break;
                case PlanterGameModel.PlanterColour.Purple:
                    voToPlay.push(VO.PP_COLOR06);
                    break;
            }

            voToPlay.push(choiceReactVO());

            HLF.Application.instance.voiceover.playVO(voToPlay, HLF.PlayMode.New, (pDidPlay: boolean) => {
                this.idleTimeline?.restart(true);
            });
        }
    }
}
